import { VisualizationManager, AccessibilityElement } from '@sas-dvr/ltjs-commons';
import { merge, Observable, EMPTY, from } from 'rxjs';
import { distinctUntilChanged, finalize, ignoreElements, map, tap } from 'rxjs/operators';
import { DataVisualElementController } from '@sas-dvr/ltjs-bird';
import { StyleAttribute } from '@sas-dvr/ltjs-commons';
import {
  getAllTextStyles,
  getColorStringForAttributeColor,
} from '@sas-dvr/ltjs-commons/util/StyleUtils';
import CssStyleBundle from '@sas-dvr/ltjs-commons/CssStyleBundle';
import { createBirdActionsHandler } from '../util/birdActions';
import { GraphDescription } from '@sas-dvr/ltjs-gtl';
import { setConstraints } from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { VisualElementControllerLoader } from '.';
import { loadDataVisualElementController } from './loadDataVisualElement';
import { getUriParameter } from '@sas-dvr/ltjs-commons/ltjsUtil';
import { getTextStyles } from './promptElementImpl/utils';
import { StyleState } from '@sas-dvr/internal-va-react-core/store/style/StyleState';

// For consistency, this flag is temporary until react VA supports this as well
const useReportThemeDatatips =
  getUriParameter('VASDK_REPORT_THEME_DATATIPS')?.toLowerCase() === 'true';

export const loadVisualization: VisualElementControllerLoader<{
  contentId$: Observable<string>;
  visualizationManager?: VisualizationManager;
  enableScrollOverlay?: boolean;
}> = (
  visualController,
  visualElementController,
  unload$,
  myActions$,
  state$,
  deps,
  lifecyleActions,
  extraDeps
) => {
  if (!(visualElementController instanceof DataVisualElementController)) {
    throw new Error('Expected the controller to be a DataVisualElementController');
  }
  if (!extraDeps?.contentId$) {
    throw new Error('contentId$ required for loadDataVisualElementController');
  }
  const { visualizationManager = new VisualizationManager(), contentId$ } = extraDeps;

  visualizationManager.setBaseView(visualElementController.getView());

  const ariaLabel = visualElementController.getAccessibilityLabel();
  if (ariaLabel) {
    visualizationManager.setCanvasRole('img');
    visualizationManager.setCanvasAriaLabel(ariaLabel);
  }

  const id = deps.getKey(visualElementController);
  const actions = [];

  //
  // Apply custom tooltips to the VisualizationManager
  //
  const tipStyleChain = visualElementController.getTipStyleChain();
  if (tipStyleChain) {
    const tooltipCss = getAllTextStyles(tipStyleChain, useReportThemeDatatips) || {};
    if (useReportThemeDatatips) {
      // background color is needed for tips in addition to text styles
      if (tipStyleChain.hasStyleAttributeValue(StyleAttribute.FILL_COLOR)) {
        tooltipCss['background-color'] = getColorStringForAttributeColor(
          tipStyleChain,
          StyleAttribute.FILL_COLOR,
          1,
          true
        );
      }
    } else {
      if (tipStyleChain.hasStyleAttributeValueAboveBase(StyleAttribute.FILL_COLOR)) {
        tooltipCss['background-color'] = getColorStringForAttributeColor(
          tipStyleChain,
          StyleAttribute.FILL_COLOR,
          1
        );
      }
    }

    visualizationManager.setCustomTooltipStyles(tooltipCss as CssStyleBundle);
  }

  const { birdActions$, birdActionsHandler } = createBirdActionsHandler(id, myActions$, state$);
  //
  // Enable double-click to drill
  //
  visualizationManager.addGestureOverride('doubleClick', (event) => {
    if (visualElementController.isBusy() || !event.sourceEvent) {
      return false;
    }
    const x = event.sourceEvent.x;
    const y = event.sourceEvent.y;

    const pickResults = visualizationManager.getPickResults(x, y, true, false);
    const tooltipEvent = visualElementController.getTooltipEventForPickResults(pickResults);
    if (tooltipEvent) {
      return birdActionsHandler(tooltipEvent, x, y);
    }
  });

  visualizationManager.setAccessibilityActionCallback((element: AccessibilityElement) => {
    if (visualElementController.isBusy()) {
      return;
    }

    const pickResult = element.getPickResult();
    if (!pickResult) {
      return;
    }

    const tooltipEvent = visualElementController.getTooltipEventForPickResults([pickResult]);
    if (!tooltipEvent) {
      return;
    }

    const location = tooltipEvent.getLocationWithinSection();
    return birdActionsHandler(tooltipEvent, location[0], location[1]);
  });

  let minSize = 100;
  // Special case: key value has a different min size.
  if (
    visualElementController.getVisualElementTypeInfo()?.getGraphDescription() ===
    GraphDescription.KEY_VALUE
  ) {
    minSize = 50;
  }
  // Special case: no min size for elements with no view controller (supplemental visuals)
  if (!visualController) {
    minSize = 0;
  }
  actions.push(setConstraints(id, { minHeight: minSize, minWidth: minSize }));

  // Set text styles for breadcrumbs
  const styleChain = visualElementController.getElementContainerStyleChain();
  if (styleChain) {
    actions.push(StyleState.actions.set(id, getTextStyles(styleChain)));
  }

  //
  // Set the container id on the VisualizationManager. This is the id of the element that the
  // visualization's canvas will be added to.
  //
  const ids$ =
    contentId$.pipe(
      tap((containerId) => visualizationManager.setContainerId(containerId)),
      ignoreElements()
    ) ?? EMPTY;

  const handleRestrictViewportChange$ = state$.pipe(
    map((state) => state.bird.restrictViewportGestures ?? !!state.bird.viewSetReport),
    distinctUntilChanged(),
    tap((restrictFromState) => {
      const restrictViewportGestures =
        !!extraDeps.enableScrollOverlay &&
        restrictFromState &&
        visualElementController.isZoomOverlaySupported();
      visualizationManager.setRestrictViewportGestures(restrictViewportGestures);
      if (restrictViewportGestures) {
        visualizationManager.setZoomOverlayStylesFromStyle(visualElementController.getStyle());
      }
    }),
    ignoreElements()
  );

  const loadVisualElement$ = loadDataVisualElementController(
    visualController,
    visualElementController,
    unload$,
    myActions$,
    state$,
    deps,
    lifecyleActions,
    visualizationManager
  ).pipe(
    finalize(() => {
      visualizationManager.destroy();
    })
  );

  return merge(
    birdActions$,
    loadVisualElement$,
    ids$,
    handleRestrictViewportChange$,
    from(actions)
  );
};
